import { useContext, useEffect, useState } from "react";
import { Table, Button, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { apiUrl } from "../config";
import TableContext from "../Context/TablePosition";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AIModelOutputsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setCurrentUrl } = useContext(TableContext);
  const location = useLocation();
  const { t } = useTranslation();

  const username = process.env.REACT_APP_USERNAME;
  const password = process.env.REACT_APP_SECRET_KEY;

  useEffect(() => {
    localStorage.setItem("urlpaintshop", location.pathname);
    setCurrentUrl(localStorage.getItem("urlpaintshop"));
  }, [location.pathname, setCurrentUrl]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      // Parametresiz çağrı: en güncel günün tüm planı, sequence sırasıyla
      const res = await axios.get(`${apiUrl}/getAIModelOutputs`, {
        auth: {
          username: username,
          password: password,
        },
      });
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.message || t("aiOutputs.error"));
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const planDate = data.length > 0 ? data[0].date : "";

  const actionFilters = Array.from(
    new Set(data.map((r) => r.action_name).filter(Boolean))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ text: v, value: v }));

  const colorFilters = Array.from(
    new Set(data.map((r) => r.color).filter(Boolean))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ text: v, value: v }));

  const columns = [
    {
      title: t("aiOutputs.date"),
      dataIndex: "date",
      key: "date",
      align: "center",
      width: 120,
      sorter: (a, b) => (a.date || "").localeCompare(b.date || ""),
    },
    {
      title: t("aiOutputs.time"),
      dataIndex: "period",
      key: "period",
      align: "center",
      width: 100,
      sorter: (a, b) => (a.period || "").localeCompare(b.period || ""),
    },
    {
      title: t("aiOutputs.action"),
      dataIndex: "action_name",
      key: "action_name",
      align: "center",
      width: 260,
      filters: actionFilters,
      onFilter: (value, record) => record.action_name === value,
      render: (text, row) =>
        row.action_code === 0 ? (
          <Tag color="default">{text || t("aiOutputs.stop")}</Tag>
        ) : (
          text
        ),
    },
    {
      title: t("aiOutputs.color"),
      dataIndex: "color",
      key: "color",
      align: "center",
      width: 120,
      filters: colorFilters,
      onFilter: (value, record) => record.color === value,
      render: (color) => color || "-",
    },
    {
      title: t("aiOutputs.quantity"),
      dataIndex: "production_qty",
      key: "production_qty",
      align: "center",
      width: 100,
      sorter: (a, b) => (a.production_qty || 0) - (b.production_qty || 0),
    },
  ];

  const dataSource = data.map((row) => ({ ...row, key: row.id }));

  return (
    <div className="flex w-full flex-col h-full">
      <div className="flex items-center justify-between mt-4 mb-2">
        <div className="text-lg font-semibold">
          {t("aiOutputs.title")}{planDate ? ` — ${planDate}` : ""}
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchData}
          loading={loading}
          className="flex items-center justify-center gap-2"
        >
          {t("aiOutputs.refresh")}
        </Button>
      </div>

      {error && <div className="text-red-500 mb-2">{error}</div>}

      <div className="flex h-full overflow-auto w-full mt-2">
        <Table
          className="ai-outputs-table"
          bordered
          sticky
          tableLayout="fixed"
          showSorterTooltip={false}
          loading={loading}
          dataSource={dataSource}
          columns={columns}
          rowClassName={(record, index) =>
            record.action_code === 0
              ? "table-row-dark table-height"
              : index % 2 === 0
              ? "table-row-light table-height"
              : "table-row-dark table-height"
          }
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20 }}
        />
      </div>
    </div>
  );
}
